"use client";
import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

export type SignupStep = {
  title: string;
  description: string;
};

export type SignupStepsProps = {
  sectionTitle?: string;
  steps?: SignupStep[];
};

const defaultSteps: SignupStep[] = [
  {
    title: "Create your BatchFinder account",
    description: "Sign up with your phone number—no credit card required. Free 3 day trial for new users.",
  },
  {
    title: "Link your Instacart account",
    description: "Securely connect your Instacart account, verify your identity, and configure your preferences.",
  },
  {
    title: "Start auto-accepting batches",
    description: "Set your location and watch us grab the best batches for you!",
  },
];

export function SignupSteps({
  sectionTitle = "Signup steps",
  steps = defaultSteps,
}: SignupStepsProps) {
  const [open, setOpen] = useState(true);

  return (
    <section className="w-full mt-0 bg-white py-6 px-4">
      {/* Header */}
      <button
        className="flex items-center gap-2 mx-auto font-bold text-lg text-primary focus:outline-none select-none"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{ minWidth: 160 }}
      >
        {sectionTitle} {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {/* Steps Timeline */}
      {open && (
        <div className="relative">
          {/* Desktop: flex row, no lines */}
          <div className="hidden md:flex items-start justify-center w-full">
            {steps.map((step, i) => (
              <div key={i} className="flex flex-col items-center flex-1 min-w-[180px] max-w-xs px-2">
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-primary bg-white text-primary font-bold text-2xl z-10">
                  {i + 1}
                </div>
                <div className="mt-4 text-center w-full">
                  <div className="font-bold text-lg text-primary mb-1">{step.title}</div>
                  <div className="text-gray-700 text-sm md:text-base">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Mobile: vertical timeline */}
          <div className="flex flex-col md:hidden gap-8 relative">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center relative">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-primary bg-white text-primary font-bold text-2xl z-10 relative">
                    {i + 1}
                    {/* Mobile vertical line connecting circles */}
                    {i < steps.length - 1 && (
                      <div className="absolute left-1/2 top-full w-0.5" style={{ height: 'calc(100% + 1.5rem)', transform: 'translateX(-50%)', backgroundColor: 'rgba(93,95,239,0.3)', zIndex: 0 }} />
                    )}
                  </div>
                </div>
                <div className="ml-4 text-left">
                  <div className="font-bold text-lg text-primary mb-1">{step.title}</div>
                  <div className="text-gray-700 text-sm">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
} 